import { useEffect, useState } from 'react';

import { DEFAULT_METHODS, fetchMethods, type AuthMethods } from './auth';

/**
 * Which sign-in methods the server is offering.
 *
 * HIDDEN BY DEFAULT: until `GET /v1/auth/methods/` has answered `email: true`
 * the "Use email instead" door is not drawn. A link to a form the server
 * answers with a 404 is worse than a missing one, and a page talking to a
 * server without the endpoint must look exactly like it did before. A failed
 * call leaves the default in place for the same reason - the switch is an
 * operational one (staff turn it off to stop a mail provider haemorrhaging),
 * and the form explains a refusal if somebody does reach it.
 *
 * `refresh` re-asks, for when the form is told the switch went off.
 */
export function useAuthMethods(): { methods: AuthMethods; refresh: () => void } {
  const [methods, setMethods] = useState<AuthMethods>(DEFAULT_METHODS);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    fetchMethods(ac.signal)
      .then((m) => {
        if (!ac.signal.aborted) setMethods(m);
      })
      .catch(() => {
        /* keep what we have: email stays hidden */
      });
    return () => ac.abort();
  }, [tick]);

  return { methods, refresh: () => setTick((n) => n + 1) };
}
